import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { 
  ArrowLeft, Calendar, ChevronLeft, ChevronRight, Clock, 
  User, CheckCircle, XCircle, RefreshCw, CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_SLOTS = [
  '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00'
];

function shiftCalendarWeek(isoDate, direction) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return null;
  const date = new Date(`${isoDate}T12:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== isoDate) return null;
  date.setUTCDate(date.getUTCDate() + direction * 7);
  return date.toISOString().slice(0, 10);
}

export default function AvailabilityCalendar() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [calendar, setCalendar] = useState([]);
  const [weekStart, setWeekStart] = useState('');
  const [weekEnd, setWeekEnd] = useState('');
  const [selectedCaregiver, setSelectedCaregiver] = useState('all');

  const isCaregiver = user?.role === 'independent_caregiver';

  useEffect(() => {
    if (user?.user_id) {
      if (isCaregiver) {
        fetchMyAvailability();
      } else {
        fetchCalendar();
      }
    }
  }, [isCaregiver, user?.user_id]);

  const fetchCalendar = async (startDate = null) => {
    setLoading(true);
    try {
      const params = startDate ? `?start_date=${startDate}` : '';
      const response = await api.get(`/caregivers/calendar/week${params}`);
      setCalendar(response.data.calendar || []);
      setWeekStart(response.data.week_start || '');
      setWeekEnd(response.data.week_end || '');
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
      // Initialize with empty calendar for families
      setCalendar([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyAvailability = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/caregivers/${user.user_id}/availability`);
      // Convert to calendar format
      const availability = response.data.availability || {};
      const weeklySlots = availability.weekly_slots || [];
      const calendarData = DAY_NAMES.map((dayName, index) => {
        const slot = weeklySlots.find(s => s.day === index);
        return {
          day_name: dayName,
          date: '',
          display_date: '',
          available: slot?.available || false,
          start_time: slot?.start_time || '09:00',
          end_time: slot?.end_time || '17:00'
        };
      });
      setCalendar(calendarData);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
      // Initialize with default availability
      const defaultCalendar = DAY_NAMES.map((dayName) => ({
        day_name: dayName,
        date: '',
        display_date: '',
        available: false,
        start_time: '09:00',
        end_time: '17:00'
      }));
      setCalendar(defaultCalendar);
    } finally {
      setLoading(false);
    }
  };

  const updateMyAvailability = async (updatedSlots) => {
    try {
      const weeklySlots = updatedSlots.map((day, index) => ({
        day: index,
        start_time: day.start_time,
        end_time: day.end_time,
        available: day.available
      }));
      
      await api.put(`/caregivers/${user.user_id}/availability`, { weekly_slots: weeklySlots });
      toast.success('Availability updated!');
      setCalendar(updatedSlots);
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  const toggleDayAvailability = (dayIndex) => {
    const updated = [...calendar];
    updated[dayIndex] = { ...updated[dayIndex], available: !updated[dayIndex].available };
    updateMyAvailability(updated);
  };

  const updateDayTime = (dayIndex, field, value) => {
    const updated = [...calendar];
    updated[dayIndex] = { ...updated[dayIndex], [field]: value };
    updateMyAvailability(updated);
  };

  const navigateWeek = (direction) => {
    const nextStart = shiftCalendarWeek(calendar[0]?.date || '', direction);
    if (nextStart) fetchCalendar(nextStart);
  };

  const getAllCaregivers = () => {
    const caregivers = new Map();
    calendar.forEach(day => {
      day.caregivers?.forEach(cg => {
        if (!caregivers.has(cg.user_id)) {
          caregivers.set(cg.user_id, cg.name);
        }
      });
    });
    return Array.from(caregivers.entries());
  };

  const filterCaregivers = (caregivers) => {
    if (selectedCaregiver === 'all') return caregivers;
    return caregivers?.filter(cg => cg.user_id === selectedCaregiver) || [];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']" data-testid="availability-title">
                {isCaregiver ? 'My Availability' : 'Caregiver Availability'}
              </h1>
              <p className="text-xs sm:text-sm text-slate-500">
                {isCaregiver ? 'Set your weekly availability schedule' : 'View when your caregivers are available'}
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Caregiver View - Edit Availability */}
        {isCaregiver ? (
          <Card>
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-teal-500" />
                Weekly Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-6">
                Set your regular weekly availability. Families will see this when looking for caregivers.
              </p>
              <div className="space-y-4">
                {calendar.map((day, index) => (
                  <div
                    key={day.day_name}
                    className={`p-3 sm:p-4 rounded-xl border ${day.available ? 'bg-teal-50 border-teal-200' : 'bg-slate-50 border-slate-200'}`}
                    data-testid={`day-${index}`}
                  >
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => toggleDayAvailability(index)}
                          className={`w-6 h-6 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${
                            day.available ? 'bg-teal-500 border-teal-500' : 'border-slate-300'
                          }`}
                          data-testid={`toggle-${index}`}
                        >
                          {day.available && <CheckCircle className="w-4 h-4 text-white" />}
                        </button>
                        <span className={`font-medium text-sm sm:text-base ${day.available ? 'text-teal-800' : 'text-slate-500'}`}>
                          {day.day_name}
                        </span>
                      </div>

                      {day.available && (
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <Select
                            value={day.start_time}
                            onValueChange={(v) => updateDayTime(index, 'start_time', v)}
                          >
                            <SelectTrigger className="w-20 sm:w-24 h-8 text-xs sm:text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TIME_SLOTS.map(time => (
                                <SelectItem key={time} value={time}>{time}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="text-slate-400 text-xs">to</span>
                          <Select
                            value={day.end_time}
                            onValueChange={(v) => updateDayTime(index, 'end_time', v)}
                          >
                            <SelectTrigger className="w-20 sm:w-24 h-8 text-xs sm:text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TIME_SLOTS.map(time => (
                                <SelectItem key={time} value={time}>{time}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {!day.available && (
                        <Badge variant="secondary" className="text-slate-500 text-xs">
                          <XCircle className="w-3 h-3" />
                          Not Available
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Family View - Calendar */
          <>
            {/* Week Navigation */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="shrink-0 h-8 w-8" onClick={() => navigateWeek(-1)} data-testid="prev-week">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="text-center min-w-0">
                  <h2 className="text-sm sm:text-base font-semibold whitespace-nowrap">{weekStart} - {weekEnd}</h2>
                  <p className="text-xs text-slate-500">Week View</p>
                </div>
                <Button variant="outline" size="icon" className="shrink-0 h-8 w-8" onClick={() => navigateWeek(1)} data-testid="next-week">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              {getAllCaregivers().length > 0 && (
                <Select value={selectedCaregiver} onValueChange={setSelectedCaregiver}>
                  <SelectTrigger className="w-full sm:w-48 h-8 text-sm" data-testid="caregiver-filter">
                    <SelectValue placeholder="All Caregivers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Caregivers</SelectItem>
                    {getAllCaregivers().map(([id, name]) => (
                      <SelectItem key={id} value={id}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Calendar Grid */}
            <Card>
              <CardContent className="p-0">
                {calendar.length === 0 || !calendar[0].caregivers?.length ? (
                  <div className="text-center py-12">
                    <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Caregivers in Your Care Circle</h3>
                    <p className="text-slate-500 mb-4">Invite caregivers to see their availability here</p>
                    <Button className="bg-teal-500 hover:bg-teal-600" onClick={() => navigate('/quick-enroll')}>
                        Invite Caregivers
                      </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-px">
                    <div className="grid grid-cols-7 divide-x" style={{ minWidth: '560px' }}>
                      {calendar.map((day, index) => (
                        <div key={index} className="min-h-[160px] sm:min-h-[200px]">
                          {/* Day Header */}
                          <div className={`p-2 sm:p-3 text-center border-b ${index === 5 || index === 6 ? 'bg-slate-50' : 'bg-white'}`}>
                            <p className="text-xs sm:text-sm font-medium text-slate-500">{day.day_name.slice(0, 3)}</p>
                            <p className="text-base sm:text-lg font-bold">{day.display_date}</p>
                          </div>

                          {/* Caregivers */}
                          <div className="p-1.5 sm:p-2 space-y-1.5 sm:space-y-2">
                            {filterCaregivers(day.caregivers).map((cg) => (
                              <div
                                key={cg.user_id}
                                className={`p-1.5 sm:p-2 rounded-lg text-xs ${
                                  cg.available ? 'bg-teal-50 border border-teal-200' : 'bg-slate-50 border border-slate-200'
                                }`}
                                data-testid={`cg-${cg.user_id}-day-${index}`}
                              >
                                <div className="flex items-center gap-1 mb-1">
                                  <User className="w-3 h-3 shrink-0" />
                                  <span className="font-medium truncate">{cg.name}</span>
                                </div>
                                {cg.available ? (
                                  <div className="flex items-center gap-1 text-teal-600">
                                    <Clock className="w-3 h-3 shrink-0" />
                                    <span className="whitespace-nowrap">{cg.start_time} - {cg.end_time}</span>
                                  </div>
                                ) : (
                                  <span className="text-slate-400 text-[10px] sm:text-xs">Not available</span>
                                )}
                                {cg.exception_reason && (
                                  <p className="text-amber-600 mt-1 truncate">{cg.exception_reason}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
